// Chat Translation Service - Converts player input into character dialogue
import { generateStoryContent } from './openRouterService';
import { UserProfile } from '@/types';

interface TranslationContext {
  playerArchetype: string;
  currentLocation: string;
  recentNarrative: string;
  worldKarma: number;
  isInRealm: boolean;
  companionsPresent: string[];
}

interface TranslatedMessage {
  originalText: string;
  translatedDialogue: string;
  characterAction?: string;
  emotionalTone: string;
  karmaImpact: number;
  isAction: boolean; // true if this should be treated as an action rather than dialogue
}

class ChatTranslationService {
  
  /**
   * Translates raw player input into character-appropriate dialogue
   * This is the core function that makes chat feel like natural character interaction
   */
  public async translatePlayerInput(
    playerInput: string,
    userProfile: UserProfile,
    context: TranslationContext
  ): Promise<TranslatedMessage> {
    try {
      console.log('[ChatTranslation] Translating player input:', playerInput.substring(0, 50));
      
      // Determine if this is an action or dialogue
      const isAction = this.detectActionIntent(playerInput);
      
      const translationPrompt = this.buildTranslationPrompt(
        playerInput,
        userProfile,
        context,
        isAction
      );

      const response = await generateStoryContent(
        translationPrompt,
        [],
        'chat-translation',
        userProfile.id
      );

      if (response && response.narrative) {
        let translationData;
        try {
          translationData = JSON.parse(response.narrative);
        } catch (parseError) {
          // Fallback if JSON parsing fails
          console.warn('[ChatTranslation] JSON parse failed, using fallback');
          translationData = this.createFallbackTranslation(playerInput, userProfile, isAction);
        }

        const translated: TranslatedMessage = {
          originalText: playerInput,
          translatedDialogue: translationData.dialogue || playerInput,
          characterAction: translationData.action,
          emotionalTone: translationData.tone || 'neutral',
          karmaImpact: translationData.karmaImpact || 0,
          isAction: isAction || translationData.isAction || false
        };

        console.log('[ChatTranslation] Translation result:', {
          original: playerInput.substring(0, 30),
          translated: translated.translatedDialogue.substring(0, 30),
          tone: translated.emotionalTone,
          isAction: translated.isAction
        });

        return translated;
      }

      // Fallback if API fails
      return this.createFallbackTranslation(playerInput, userProfile, isAction);

    } catch (error) {
      console.error('[ChatTranslation] Translation failed:', error);
      return this.createFallbackTranslation(playerInput, userProfile, false);
    }
  }

  private buildTranslationPrompt(
    playerInput: string,
    userProfile: UserProfile,
    context: TranslationContext,
    isAction: boolean
  ): string {
    const characterName = userProfile.displayName || 'Unknown';
    const archetype = context.playerArchetype || 'cyberpunk-hacker';
    
    return `
You are translating raw player input into natural character dialogue for a cyberpunk RPG.

CHARACTER CONTEXT:
- Name: ${characterName}
- Archetype: ${archetype}
- Location: ${context.currentLocation}
- World Karma: ${context.worldKarma}
- Context: ${context.isInRealm ? 'In a Realm (actions possible)' : 'In World (dialogue only)'}
- Recent Story: ${context.recentNarrative.substring(0, 200)}...
- Companions Present: ${context.companionsPresent.join(', ')}

PLAYER INPUT: "${playerInput}"
INPUT TYPE: ${isAction ? 'ACTION' : 'DIALOGUE'}

TRANSLATION RULES:
1. Convert the input into natural character speech that fits the cyberpunk setting
2. Maintain the player's intent but make it sound like the character would say it
3. Consider the archetype's personality and speaking style
4. If it's an action, describe what the character does
5. Assign appropriate emotional tone and karma impact

ARCHETYPE SPEAKING STYLES:
- cyberpunk-hacker: Technical, street-smart, uses cyber slang
- glitch-runner: Energetic, risk-taking, uses movement metaphors  
- synapse-surfer: Intuitive, philosophical, uses neural/dream language
- chrome-shaman: Mystical, wise, blends tech and spirituality

Respond with JSON:
{
  "dialogue": "What the character says (if dialogue)",
  "action": "What the character does (if action)",
  "tone": "emotional tone (confident|cautious|aggressive|friendly|mysterious|etc)",
  "karmaImpact": number (-10 to +10),
  "isAction": boolean,
  "reasoning": "Brief explanation of translation choices"
}

Examples:
Input: "hey jax what's up" 
→ {"dialogue": "JAX, status report. What's the current situation?", "tone": "casual", "karmaImpact": 0, "isAction": false}

Input: "i want to hack that terminal"
→ {"action": "I approach the terminal, cyberdeck ready, and begin probing its security protocols", "tone": "focused", "karmaImpact": -2, "isAction": true}

Input: "let's be friends"
→ {"dialogue": "I think we could work well together. Trust is valuable in this city.", "tone": "friendly", "karmaImpact": 3, "isAction": false}
`;
  }

  private detectActionIntent(input: string): boolean {
    const actionKeywords = [
      'hack', 'attack', 'steal', 'break', 'run', 'jump', 'climb', 'hide',
      'search', 'investigate', 'examine', 'use', 'take', 'grab', 'open',
      'close', 'activate', 'disable', 'connect', 'disconnect', 'scan',
      'infiltrate', 'escape', 'fight', 'defend', 'shoot', 'throw',
      'i want to', 'let me', 'i will', 'i should', 'i need to'
    ];

    const lowerInput = input.toLowerCase();
    return actionKeywords.some(keyword => lowerInput.includes(keyword));
  }

  private createFallbackTranslation(
    playerInput: string,
    userProfile: UserProfile,
    isAction: boolean
  ): TranslatedMessage {
    // Simple fallback translation
    let translatedDialogue = playerInput;
    
    // Basic archetype-based translation
    const archetype = userProfile.selectedArchetype || 'cyberpunk-hacker';
    
    if (archetype === 'cyberpunk-hacker') {
      translatedDialogue = this.addCyberSlang(playerInput);
    } else if (archetype === 'glitch-runner') {
      translatedDialogue = this.addMovementFlavor(playerInput);
    } else if (archetype === 'synapse-surfer') {
      translatedDialogue = this.addNeuralFlavor(playerInput);
    } else if (archetype === 'chrome-shaman') {
      translatedDialogue = this.addMysticalFlavor(playerInput);
    }

    return {
      originalText: playerInput,
      translatedDialogue: translatedDialogue,
      emotionalTone: 'neutral',
      karmaImpact: 0,
      isAction: isAction
    };
  }

  private addCyberSlang(input: string): string {
    // Add cyberpunk flavor to dialogue
    return input
      .replace(/\bhello\b/gi, 'Greetings, choom')
      .replace(/\byes\b/gi, 'Affirmative')
      .replace(/\bno\b/gi, 'Negative')
      .replace(/\bokay\b/gi, 'Roger that')
      .replace(/\bcomputer\b/gi, 'system')
      .replace(/\bphone\b/gi, 'comm device');
  }

  private addMovementFlavor(input: string): string {
    // Add movement/speed metaphors
    return input
      .replace(/\bquickly\b/gi, 'at full sprint')
      .replace(/\bfast\b/gi, 'lightning quick')
      .replace(/\bslow\b/gi, 'at a crawl')
      .replace(/\bgo\b/gi, 'dash');
  }

  private addNeuralFlavor(input: string): string {
    // Add neural/dream language
    return input
      .replace(/\bthink\b/gi, 'process')
      .replace(/\bfeel\b/gi, 'sense in the neural stream')
      .replace(/\bknow\b/gi, 'have data on')
      .replace(/\bremember\b/gi, 'access memory banks');
  }

  private addMysticalFlavor(input: string): string {
    // Add mystical/spiritual elements
    return input
      .replace(/\bsee\b/gi, 'perceive')
      .replace(/\bunderstand\b/gi, 'comprehend the deeper meaning')
      .replace(/\bhelp\b/gi, 'guide')
      .replace(/\bfind\b/gi, 'seek');
  }

  /**
   * Determines the karma impact of different types of messages
   */
  public calculateKarmaImpact(translatedMessage: TranslatedMessage, context: TranslationContext): number {
    let karmaImpact = translatedMessage.karmaImpact;

    // Modify based on context
    if (context.isInRealm) {
      // Actions in realms have higher karma impact
      karmaImpact *= 1.5;
    } else {
      // World chat has lower karma impact
      karmaImpact *= 0.5;
    }

    // Modify based on emotional tone
    switch (translatedMessage.emotionalTone) {
      case 'aggressive':
      case 'hostile':
        karmaImpact -= 2;
        break;
      case 'friendly':
      case 'helpful':
        karmaImpact += 2;
        break;
      case 'mysterious':
      case 'cautious':
        karmaImpact += 1;
        break;
    }

    return Math.max(-10, Math.min(10, Math.round(karmaImpact)));
  }

  /**
   * Validates if a message is appropriate for the current context
   */
  public validateMessage(translatedMessage: TranslatedMessage, context: TranslationContext): {
    isValid: boolean;
    reason?: string;
  } {
    // Check if actions are allowed in current context
    if (translatedMessage.isAction && !context.isInRealm) {
      return {
        isValid: false,
        reason: 'Actions can only be performed in Realms. Use World chat for dialogue.'
      };
    }

    // Check for inappropriate content (basic filter)
    const inappropriate = ['spam', 'abuse', 'harassment'];
    const lowerText = translatedMessage.translatedDialogue.toLowerCase();
    
    if (inappropriate.some(word => lowerText.includes(word))) {
      return {
        isValid: false,
        reason: 'Message contains inappropriate content.'
      };
    }

    return { isValid: true };
  }

  /**
   * Get suggested responses based on the translated message
   */
  public getSuggestedResponses(translatedMessage: TranslatedMessage): string[] {
    const suggestions: string[] = [];

    if (translatedMessage.isAction) {
      suggestions.push(
        'Continue the action',
        'Change approach',
        'Ask for help'
      );
    } else {
      switch (translatedMessage.emotionalTone) {
        case 'friendly':
          suggestions.push('Ask a question', 'Share information', 'Offer help');
          break;
        case 'aggressive':
          suggestions.push('Stand your ground', 'Try to de-escalate', 'Walk away');
          break;
        case 'mysterious':
          suggestions.push('Probe deeper', 'Share a secret', 'Change subject');
          break;
        default:
          suggestions.push('Continue conversation', 'Ask about the area', 'Check status');
      }
    }

    return suggestions;
  }
}

// Export singleton instance
export const chatTranslationService = new ChatTranslationService();